import { Fragment } from 'react';

export const customIntegrationInstructionMap = {
  github_server: {
    title: 'GitHub Enterprise',
    description:
      'Authorize Codekeeper to securely connect to your GitHub Enterprise account using an access token. Once connected, you can choose which repositories to deposit, and Codekeeper will automatically monitor them for updates.',
    items: [
      <Fragment key="1">
        In your GitHub Enterprise account, go to{' '}
        <span className="font-bold">
          Settings → Developer Settings → Personal access tokens → Personal access tokens (classic).
        </span>
      </Fragment>,
      <Fragment key="2">
        Click <strong>Generate new token</strong> button, then set <strong>Expiration</strong> to{' '}
        <strong>No Expiration</strong>.
      </Fragment>,
      <Fragment key="3">
        Select the following scopes:
        <ul className="list-disc list-inside font-bold">
          <li>repo</li>
          <li>read:user</li>
          <li>user:email</li>
        </ul>
        <br />
        Then click the <strong>Generate token</strong> button.
      </Fragment>,
      <span className="font-bold" key="4">
        Copy your new access token.
      </span>,
      'Click the button below to set up the integration with your access credentials.',
    ],
  },
  gitlab_server: {
    title: 'GitLab Self-Managed',
    description:
      'Authorize Codekeeper to securely connect to your GitLab Self-Managed account using an access token. Once connected, you can choose which repositories to deposit, and Codekeeper will automatically monitor them for updates.',
    items: [
      <Fragment key="1">
        In your GitLab Self-Managed account, go to{' '}
        <span className="font-bold">Preferences → Access tokens → Add new token.</span>
      </Fragment>,
      <Fragment key="2">
        Select the following scopes:
        <ul className="list-disc list-inside font-bold">
          <li>read_api</li>
          <li>read_user</li>
          <li>read_repository</li>
          <li>read_registry</li>
        </ul>
        <br />
        Then click the <strong>Create Personal Access Token</strong> button.
      </Fragment>,
      <span className="font-bold" key="3">
        Copy your new access token.
      </span>,
      'Click the button below to set up the integration with your access credentials.',
    ],
  },
  bitbucket_server: {
    title: 'Bitbucket Server',
    description:
      'Authorize Codekeeper to securely connect to your Bitbucket Server account using an app password. Once connected, you can choose which repositories to deposit, and Codekeeper will automatically monitor them for updates.',
    items: [
      <Fragment key="1">
        In your Bitbucket Server account, go to{' '}
        <span className="font-bold">Settings → Personal Settings → App passwords → Create app password.</span>
      </Fragment>,
      <Fragment key="2">
        Enter a name for your app password in the <span className="font-bold">Label</span> field.
      </Fragment>,
      <Fragment key="3">
        Select the following permissions for the app password:
        <ul className="list-disc list-inside font-bold">
          <li>Account (email)</li>
          <li>Workspace membership (read)</li>
          <li>Projects (read)</li>
          <li>Repositories (read)</li>
        </ul>
        <br />
        Then click on <strong>Create</strong>.
      </Fragment>,
      <span className="font-bold" key="4">
        Copy your new app password.
      </span>,
      'Click the button below to set up the integration with your access credentials.',
    ],
  },
  codecommit: {
    title: 'Amazon CodeCommit',
    description:
      'Authorize Codekeeper to securely connect to your Amazon CodeCommit account using an access policy. Once connected, you can choose which repositories to deposit, and Codekeeper will automatically monitor them for updates.',
    items: [
      <Fragment key="1">
        In your Amazon CodeCommit account, go to{' '}
        <span className="font-bold">IAM → Users → Policies → Create Policy.</span>
      </Fragment>,
      <Fragment key="2">
        Create an access policy with <span className="font-bold">codecommit:GetRepository</span> and{' '}
        <span className="font-bold">codecommit:GitPull</span> access.
      </Fragment>,
      <Fragment key="3">
        Go to <span className="font-bold">IAM → Users → Policies → Add users.</span>
      </Fragment>,
      <Fragment key="4">
        Create a user with the <strong className="font-bold">Access key - Programmatic access</strong> credentials type,
        then copy and attach the policy you created in Step 2 to the user.
      </Fragment>,
      <Fragment key="5">
        Go to the <span className="font-bold">user details</span> page{' '}
        <span className="font-bold">→ Security Credentials → HTTPS Git credentials for AWS CodeCommit → Generate.</span>
      </Fragment>,
      <Fragment key="6">
        <span className="font-bold">Copy the username and password </span> that IAM generated for you.
      </Fragment>,
      'Click the button below to set up the integration with your access credentials.',
    ],
  },
  beanstalk: {
    title: 'Beanstalk',
    description:
      'Authorize Codekeeper to securely connect to your Beanstalk account. Once connected, you can choose which repositories to deposit, and Codekeeper will automatically monitor them for updates.',
    items: ['Click the button below to set up the integration with your access credentials.'],
  },
  aws: {
    title: 'Amazon S3',
    description:
      'Authorize Codekeeper to securely connect to your Amazon S3 account using an access policy. Once connected, you can choose which buckets to deposit, and Codekeeper will monitor the assets for updates automatically.',
    items: [
      <Fragment key="1">
        In your Amazon S3 account, go to <span className="font-bold">IAM → Users → Policies → Create Policy.</span>
      </Fragment>,
      <Fragment key="2">
        Create an access policy for your bucket with <strong className="font-bold">ListBucket</strong> and
        <strong className="font-bold"> GetObject</strong> access.
      </Fragment>,
      <Fragment key="3">
        Go to <span className="font-bold">IAM → Users → Policies → Add users.</span>
      </Fragment>,
      <Fragment key="4">
        Create a user with the <strong className="font-bold">Access key - Programmatic access</strong> credentials type,
        then copy and attach the policy you created in Step 2 to the user.
      </Fragment>,
      <Fragment key="5">
        <span className="font-bold">Copy the Access Key ID and Secret Access Key</span> generated for you.
      </Fragment>,
      'Click the button below to set up the integration with your access credentials.',
    ],
  },
};

export const customIntegrationInstructionUpdateMap = Object.fromEntries(
  Object.entries(customIntegrationInstructionMap).map(([key, value]) => [
    key,
    {
      ...value,
      items:
        key === 'beanstalk'
          ? ['Click the button below to update the integration with your access credentials.']
          : value.items.slice(0, -1),
    },
  ]),
) as typeof customIntegrationInstructionMap;
